import { useCallback, useMemo, useRef } from 'react'

import {
  F0DialogPrimaryAction,
  F0DialogPrimaryActionItem,
  F0DialogSecondaryAction,
} from '@factorialco/f0-react'
import { TasksValueObjectsTaskStatusEnum } from 'generated/resources/datatable.graphql'
import { TasksValueObjectsTaskKindEnum } from 'generated/resources/graphql'

import { useActivity } from 'modules/core/lib/activities/activity'

import { getTaskActions } from 'modules/inbox_tasks/lib/actions'
import { TaskActionsHookResult } from 'modules/inbox_tasks/lib/abstraction'
import { trackClickInboxAction } from 'modules/inbox_tasks/lib/events'

type UseTaskActionsParams = {
  taskId: string
  taskKind?: TasksValueObjectsTaskKindEnum
  status?: TasksValueObjectsTaskStatusEnum
} & TaskActionsHookResult

export function useTaskActions({
  taskId,
  taskKind,
  status,
  onClickPrimaryAction,
  onClickSecondaryAction,
  onClickDropdownAction,
  actionsDisabled = false,
  approvableTypename,
  approvalSuggestion,
  dropdownActions: customDropdownActions,
}: UseTaskActionsParams) {
  const { closeActivity } = useActivity()

  // Stabilize external callbacks via refs to prevent cascading re-memoization
  const callbackRefs = useRef({
    onClickPrimaryAction,
    onClickSecondaryAction,
    onClickDropdownAction,
    closeActivity,
  })
  callbackRefs.current = {
    onClickPrimaryAction,
    onClickSecondaryAction,
    onClickDropdownAction,
    closeActivity,
  }

  const {
    primaryAction,
    secondaryAction,
    dropdownActions: defaultDropdownActions,
  } = useMemo(
    () =>
      getTaskActions({
        taskKind: taskKind ?? 'manual',
        status,
      }),
    [taskKind, status]
  )

  // Stabilize dropdownActions reference — only recompute when item values change
  const rawDropdownActions = customDropdownActions ?? defaultDropdownActions
  const dropdownActionsKey = rawDropdownActions?.map((d) => d.value).join(',')
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const dropdownActions = useMemo(() => rawDropdownActions, [dropdownActionsKey])

  // Boolean flags for callback presence — avoids unstable function refs in useMemo deps
  const hasPrimaryCallback = !!onClickPrimaryAction
  const hasSecondaryCallback = !!onClickSecondaryAction

  const handlePrimaryAction = useCallback(async () => {
    if (!primaryAction) return

    trackClickInboxAction({
      type: primaryAction.id,
      approvable_typename: approvableTypename,
      approval_suggestion: approvalSuggestion,
    })

    const resolved = await callbackRefs.current.onClickPrimaryAction?.({ taskId })
    if (resolved) {
      callbackRefs.current.closeActivity()
    }
  }, [primaryAction, taskId, approvableTypename, approvalSuggestion])

  const handleSecondaryAction = useCallback(async () => {
    if (!secondaryAction) return

    trackClickInboxAction({
      type: secondaryAction.id,
      approvable_typename: approvableTypename,
      approval_suggestion: approvalSuggestion,
    })

    const resolved = await callbackRefs.current.onClickSecondaryAction?.({ taskId })
    if (resolved) {
      callbackRefs.current.closeActivity()
    }
  }, [secondaryAction, taskId, approvableTypename, approvalSuggestion])

  const handleDropdownAction = useCallback(
    async (actionId: string) => {
      trackClickInboxAction({
        type: actionId,
        approvable_typename: approvableTypename,
        approval_suggestion: approvalSuggestion,
      })

      const resolved = await callbackRefs.current.onClickDropdownAction?.({ taskId, actionId })
      if (resolved) {
        callbackRefs.current.closeActivity()
      }
    },
    [taskId, approvableTypename, approvalSuggestion]
  )

  const primaryDialogAction = useMemo<
    F0DialogPrimaryAction | F0DialogPrimaryActionItem[] | undefined
  >(() => {
    const hasDropdown = dropdownActions && dropdownActions.length > 0

    if (primaryAction && hasPrimaryCallback && hasDropdown) {
      // Combine primary + dropdown into multiple primary action items
      return [
        {
          value: primaryAction.id,
          label: primaryAction.label,
          icon: primaryAction.icon,
          disabled: actionsDisabled,
          onClick: handlePrimaryAction,
        },
        ...dropdownActions.map((item) => ({
          value: item.value,
          label: item.label,
          icon: item.icon,
          disabled: actionsDisabled,
          onClick: () => handleDropdownAction(item.value),
        })),
      ]
    }

    if (hasDropdown) {
      // Only dropdown actions (e.g. manual tasks)
      return dropdownActions.map((item) => ({
        value: item.value,
        label: item.label,
        icon: item.icon,
        disabled: actionsDisabled,
        onClick: () => handleDropdownAction(item.value),
      }))
    }

    if (primaryAction && hasPrimaryCallback) {
      return {
        label: primaryAction.label,
        icon: primaryAction.icon,
        disabled: actionsDisabled,
        onClick: handlePrimaryAction,
      }
    }

    return undefined
  }, [
    primaryAction,
    hasPrimaryCallback,
    dropdownActions,
    actionsDisabled,
    handlePrimaryAction,
    handleDropdownAction,
  ])

  const secondaryDialogAction = useMemo<F0DialogSecondaryAction | undefined>(
    () =>
      secondaryAction && hasSecondaryCallback
        ? {
            label: secondaryAction.label,
            icon: secondaryAction.icon,
            disabled: actionsDisabled,
            onClick: handleSecondaryAction,
          }
        : undefined,
    [secondaryAction, hasSecondaryCallback, actionsDisabled, handleSecondaryAction]
  )

  return {
    primaryDialogAction,
    secondaryDialogAction,
  }
}
